"use client";

import { faClapperboardPlay } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Slot } from "radix-ui";
import { type ComponentProps, Fragment, type ReactNode, Suspense, useEffect, useMemo } from "react";
import TagManager from "react-gtm-module";
import { Dialog, type DialogProps, cn, useModal } from "../..";

interface VideoProps {
	variant: "youtube" | "vimeo";
}

interface VideoPlayerProps extends VideoProps, ComponentProps<"iframe"> {
	title: string;
	id: string;
	active?: boolean;
	autoPlay?: boolean;
	time?: number; // in seconds
	wrapperClassName?: string;
}

export function VideoPlayer(props: VideoPlayerProps) {
	const {
		title,
		variant,
		id,
		active,
		autoPlay,
		time,
		wrapperClassName,
		className,
		...remainingProps
	} = props;

	useEffect(() => {
		if (active) {
			const timeout = setTimeout(() => {
				return TagManager.dataLayer({ dataLayer: { event: "watchVideo" } });
			}, 5000);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [active]);

	const timeOption = useMemo(() => {
		if (time) {
			if (variant === "vimeo") {
				const minutes = Math.floor(time / 60);
				const seconds = time % 60;
				let options = "";

				if (minutes) {
					options += `${minutes}m`;
				}

				if (seconds) {
					options += `${seconds}s`;
				}

				return `#t=${options}`;
			}

			return `start=${time}`;
		}
	}, [time, variant]);

	const autoPlayOption = useMemo(() => {
		if (autoPlay && active) {
			if (variant === "vimeo") {
				return "autoplay=true";
			}

			return "autoplay=1";
		}
	}, [active, autoPlay, variant]);

	const src = useMemo(() => {
		const options = [timeOption, autoPlayOption].filter(Boolean).join("&amp;");

		if (variant === "vimeo") {
			return `https://player.vimeo.com/video/${id}?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479${options}`;
		}

		return `https://www.youtube.com/embed/${id}${options.length ? `?${options}` : ""}`;
	}, [autoPlayOption, id, timeOption, variant]);

	return (
		<Suspense fallback={null}>
			<div className={cn("aspect-video overflow-hidden", wrapperClassName)}>
				<div
					style={{
						position: "relative",
						paddingBottom: "calc(9 / 16 * 100%)",
					}}
				>
					{variant === "vimeo" ? (
						<>
							<iframe
								src={src}
								title={title}
								frameBorder="0"
								allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
								className={cn("absolute h-full w-full border-0", className)}
								{...remainingProps}
							/>

							<script src="https://player.vimeo.com/api/player.js" async />
						</>
					) : (
						<iframe
							src={src}
							title={title}
							frameBorder="0"
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
							referrerPolicy="strict-origin-when-cross-origin"
							allowFullScreen
							className={cn("absolute h-full w-full border-0", className)}
							{...remainingProps}
						/>
					)}
				</div>
			</div>
		</Suspense>
	);
}

interface VideoDialogProps extends Omit<DialogProps, "open" | "onOpenChange"> {
	content?: ReactNode;
	player: VideoPlayerProps;
}

export function VideoPlayerDialog(props: VideoDialogProps) {
	const { player, content, children, ...remainingProps } = props;
	const dialog = useModal();

	return (
		<Fragment>
			<Slot.Root
				onClick={(e) => {
					e.stopPropagation();
					dialog.toggle();
				}}
			>
				{children}
			</Slot.Root>

			<Dialog {...remainingProps} {...dialog.props}>
				<VideoPlayer {...player} />
				{content}
			</Dialog>
		</Fragment>
	);
}

interface VideoPlaceholderProps extends VideoProps, ComponentProps<"div"> {
	id: string;
	alt?: string;
}

export function VideoPlaceholder(props: VideoPlaceholderProps) {
	const { variant, id, alt, className, ...remainingProps } = props;

	const src =
		variant === "vimeo"
			? `https://vumbnail.com/${id}.jpg`
			: `https://img.youtube.com/vi/${id}/sddefault.jpg`;

	return (
		<div className={cn("group relative cursor-pointer text-2xl", className)} {...remainingProps}>
			<img
				src={src}
				alt={alt ?? remainingProps.title}
				className="absolute inset-0 h-full w-full object-cover"
			/>

			<span className="-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 flex h-[2em] w-[2em] items-center justify-center rounded-full bg-black/70">
				<FontAwesomeIcon
					icon={faClapperboardPlay}
					className="text-white group-hover:text-primary group-focus-visible:text-primary"
				/>
			</span>
		</div>
	);
}
